import React, { Component, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { AvatarDetails } from '../../../../model/types';
import messages from './messages';

export type Props = {
	isEnabled: boolean;
	/**
	 * Avatar that will be shown in the UI.
	 */
	avatar: AvatarDetails | undefined;
	onHasLoadedImage: () => void;
};

type PropsWithIntl = Props & {
	intl: IntlShape;
};

// eslint-disable-next-line jira/react/no-class-components
export class AvatarImage extends Component<PropsWithIntl> {
	// allows us to mock the value of Image
	static createImageNode() {
		return new Image();
	}

	componentDidMount() {
		this.prefetchImage();
	}

	componentDidUpdate(prevProps: PropsWithIntl) {
		if (this.props.avatar !== prevProps.avatar) {
			this.prefetchImage();
		}
		// where did the `} else {` go?
		// If the avatar instance is the same, it means that user has selected the same avatar twice.
		// This means that the onHasLoadedImage() will never be called.
		// That's OK because the project-details/state/ui/reducer.js has a fix for it.
	}

	prefetchImage() {
		const { avatar, onHasLoadedImage } = this.props;
		if (avatar) {
			// Since this component is called Image, we need a new namespace
			const element = AvatarImage.createImageNode();
			element.src = avatar.avatarUrl;
			// check if the image is already downloaded
			if (element.complete) {
				onHasLoadedImage();
			} else {
				const onload = () => {
					onHasLoadedImage();
				};
				element.onload = onload;
				// If avatar fails to load, also send the same event. Why? Because that information is used to enable
				// "Upload new avatar" button so if it is never loaded the UI is blocked and users cannot update their avatars.
				// https://donut-world-dogfood.atlassian.net/browse/MC-2207
				element.onerror = onload;
			}
		}
	}

	render() {
		const {
			intl: { formatMessage },
			avatar,
			isEnabled,
		} = this.props;

		return (
			<StyledAvatarImageWrapper>
				{avatar && isEnabled ? (
					<StyledAvatarImage src={avatar.avatarUrl} alt={formatMessage(messages.avatarAlt)} />
				) : (
					<EmptyImage />
				)}
			</StyledAvatarImageWrapper>
		);
	}
}

export default injectIntl(AvatarImage);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledAvatarImage = styled.img<{
	children?: ReactNode;
	src: string;
	alt: string;
}>({
	borderRadius: '3px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 16}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 16}px`,
	margin: '0 auto',
});

// TODO: https://product-fabric.atlassian.net/browse/DSP-8582
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmptyImage = styled.div<{
	children?: ReactNode;
}>({
	borderRadius: '3px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 16}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 16}px`,
	margin: '0 auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.purple.subtle', colors.N20),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledAvatarImageWrapper = styled.div<{
	children?: ReactNode;
}>({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	paddingBottom: token('space.050', '4px'),
});
